import { Region, SettingKeyType, Sort } from '@crew/enums/app'
import { useTranslation } from '@crew/modules/i18n'
import { useUserSetting } from '@crew/states'
import {
  ColumnDef,
  OnChangeFn,
  PaginationState,
  SortingState,
  TableOptions,
  getCoreRowModel,
} from '@tanstack/react-table'
import { CrewAvatarSize } from 'components/elements/crewAvatar/crewAvatar'
import { CrewTable } from 'components/elements/crewTable/crewTable'
import { CrewUserAvatar } from 'components/elements/crewUserAvatar/crewUserAvatar'
import { DEFAULT_PAGING_PARAMS } from 'configs/constants'
import _ from 'lodash'
import qs from 'qs'
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAppSelector } from 'states/hooks'
import { getParamAsArray, getParamAsString } from 'utils'
import { useTaskDetailRelatedInfoListContext } from '../../../../taskDetailRelatedInfoList'
import BaselineDelete from '~icons/ic/baseline-delete'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { useModal } from 'components/layouts/modal/useModal'
import { CrewConfirmDialog } from 'components/elements/crewConfirmDialog/crewConfirmDialog'
import { useToast } from 'hooks/useToast'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useRelatedInfoTable } from './useRelatedInfoTable'
import { useGetEntityReferencesQuery } from '@crew/apis/entityReference/entityReferenceApis'
import { EntityType } from '@crew/enums/domain'
import { EntityReference } from '@crew/apis/entityReference/models/getEntityReferences/response'
import { useProjectPermissions } from '@crew/hooks'
import { CrewEntityLink } from 'components/elements/crewEntityLink/crewEntityLink'

// renderとして使うのでmemo不可
// 更新日時
const UpdateAt: FC<{ date: string }> = (props) => {
  const { t } = useTranslation()

  // ユーザー設定からデフォルトのユーザープロファイル地域を取得
  const defaultUserProfileRegion = useUserSetting(
    SettingKeyType.UserProfileRegion,
    Region.Japan.value
  )

  return (
    <>
      {props
        ? t('format.timestamp', {
            value: props.date,
            timeZone: defaultUserProfileRegion,
          })
        : undefined}
    </>
  )
}

export const RelatedInfoTable = memo(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { success } = useToast()
  const [showApiErrors] = useShowApiErrors()
  const { taskId } = useParams()

  if (!taskId) {
    throw new Error('taskId is undefined')
  }

  const { hasPrjTaskEditPermission } = useProjectPermissions(
    EntityType.Task,
    taskId
  )

  const { isLoadingDeleteReference, deleteEntityReference } =
    useRelatedInfoTable()

  // 表示状態を取得
  const { displayState } = useTaskDetailRelatedInfoListContext()

  // パラメータ
  const [searchParams] = useSearchParams()
  const params = qs.parse(searchParams.toString())

  // ソート順。指定されていない場合は関連先の昇順
  const sortParams = useMemo(() => {
    const sortParams = searchParams.getAll('sort')
    return sortParams.length > 0
      ? sortParams
      : [`displayName.${Sort.Ascending.value}`]
  }, [searchParams])

  // ページング関連
  const defaultListDisplayNumber = useUserSetting(
    SettingKeyType.ListDisplayNumber,
    DEFAULT_PAGING_PARAMS.pageSize
  )

  const pagination: PaginationState = useMemo(
    () => ({
      pageIndex: Number(
        getParamAsString('pageIndex', params) ?? DEFAULT_PAGING_PARAMS.pageIndex
      ),
      pageSize: Number(
        getParamAsString('pageSize', params) ?? defaultListDisplayNumber
      ),
    }),
    [defaultListDisplayNumber, params]
  )

  const sorting: SortingState = useMemo(() => {
    const sortArray = getParamAsArray('sort', params) || []
    return sortArray.map((sort) => {
      const [id, direction] = sort.split('.')
      return {
        id,
        desc: direction === 'desc',
      }
    })
  }, [params])

  //関連情報取得API
  const { data: getEntityReferenceResponse, refetch: refetchEntityReferences } =
    useGetEntityReferencesQuery({
      sourceEntityRecordId: taskId,
      sourceEntityType: EntityType.Task,
      limit: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
      sort: sortParams,
    })

  const entityReferenceEventMessage = useAppSelector(
    (state) => state.app.entityReferenceEventMessage
  )

  // リストの再取得
  useEffect(() => {
    refetchEntityReferences()
  }, [entityReferenceEventMessage, refetchEntityReferences])

  // 選択中の関連情報ID（削除処理に使用）
  const [selectedReferenceId, setSelectedReferenceId] = useState<string | null>(
    null
  )
  // 削除確認ダイアログ
  const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] =
    useModal()

  // 削除アイコンクリック時の処理
  const handleOpenConfirmDeleteReferenceButtonClick = useCallback(
    (id: string) => {
      setSelectedReferenceId(id)
      openConfirmDialog()
    },
    [openConfirmDialog]
  )
  // 削除確認ダイアログ OKボタン
  const handleDeletePermitButtonClick = useCallback(async () => {
    if (!selectedReferenceId) {
      closeConfirmDialog()
      return
    }

    try {
      await deleteEntityReference(selectedReferenceId)
      setSelectedReferenceId(null)
      success(t('message.entityReference.entityReferenceDeleted'))
    } catch (err) {
      showApiErrors(err)
    }

    closeConfirmDialog()
  }, [
    closeConfirmDialog,
    deleteEntityReference,
    selectedReferenceId,
    showApiErrors,
    success,
    t,
  ])

  const pageCount = Math.ceil(
    (getEntityReferenceResponse?.totalCount ?? 0) / pagination.pageSize
  )
  const [columnVisibility, setColumnVisibility] = useState({})
  const [columnPinning] = useState({
    left: ['select'],
    right: ['action'],
  })

  const columns = useMemo<ColumnDef<EntityReference>[]>(
    () => [
      {
        id: 'displayName',
        accessorKey: 'displayName',
        header: () => t('label.relatedItem'),
        cell: ({ row }) => (
          <div className="flex flex-row items-center truncate">
            <CrewEntityLink
              id={row.original.destinationEntityRecordId}
              displayName={row.original.displayName}
              entityType={row.original.destinationEntityType}
              showEntityIcon={true}
              chatMessageInfo={row.original.chatMessageInfo ?? undefined}
            />
          </div>
        ),
        size: 500,
        minSize: 50,
      },
      // 更新者
      {
        // ソートに使用するため「updatedBy」ではなく「updatedByDisplayName」と指定する
        // ここはBackend側のORDER句に渡されるため、Select時のAS句と合わせる必要がある
        id: 'updatedByDisplayName',
        accessorKey: 'updatedByDisplayName',
        header: t('label.updatedBy'),
        cell: ({ row }) => (
          <div className="truncate">
            <CrewUserAvatar
              userId={row.original.updatedBy.id}
              displayName={row.original.updatedBy.displayName}
              cacheValue={
                row.original.updatedBy.id + row.original.updatedBy.version
              }
              size={CrewAvatarSize.xs}
              showLabel={true}
              disableClick={true}
            />
          </div>
        ),
        size: 140,
        minSize: 120,
        enableSorting: true,
      },
      // 更新日時
      {
        id: 'updatedAt',
        accessorKey: 'updatedAt',
        header: t('label.updateDatetime'),
        cell: ({ row }) => (
          <div className="truncate">
            <UpdateAt date={row.original.updatedAt} />
          </div>
        ),
        size: 160,
        minSize: 50,
        enableSorting: true,
      },
      // 削除
      {
        id: 'action',
        accessorKey: 'action',
        header: '',
        cell: ({ row }) => (
          <div>
            {hasPrjTaskEditPermission && (
              <CrewButton
                stylingMode="text"
                icon={<BaselineDelete width={20} height={20} />}
                onClick={() =>
                  handleOpenConfirmDeleteReferenceButtonClick(row.original.id)
                }
              />
            )}
          </div>
        ),
        size: 50,
        minSize: 50,
        enableSorting: false,
      },
    ],
    [handleOpenConfirmDeleteReferenceButtonClick, hasPrjTaskEditPermission, t]
  )

  // https://github.com/TanStack/table/discussions/3899
  // https://github.com/TanStack/table/discussions/3619
  // https://github.com/infonomic/remix.infonomic.io/blob/d3a7f628d3ad6e1e80cc80d4ac72db74da90e8d6/app/routes/admin%2B/users.tsx#L116
  // Func handle change pagination
  const handlePaginationChange: OnChangeFn<PaginationState> = useCallback(
    (updaterOrValue) => {
      let values: PaginationState
      if (updaterOrValue instanceof Function) {
        values = updaterOrValue(pagination)
      } else {
        values = updaterOrValue
      }

      const newParams = {
        ...params,
        pageIndex: values.pageIndex,
        pageSize: values.pageSize,
      }

      // paramsが変わっていない場合はnavigateしない
      if (_.isEqual(params, newParams)) return

      const newQueryString = qs.stringify(newParams, {
        arrayFormat: 'repeat',
        skipNulls: true,
      })

      navigate(`?${newQueryString}`)
    },
    [navigate, pagination, params]
  )

  // Func handle change sorting
  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      let values: SortingState
      if (updaterOrValue instanceof Function) {
        values = updaterOrValue(sorting)
      } else {
        values = updaterOrValue
      }

      const sortList = values.map((sort) => {
        return `${sort.id}.${sort.desc ? 'desc' : 'asc'}`
      })

      const newParams = {
        ...params,
        sort: sortList,
      }

      // paramsが変わっていない場合はnavigateしない
      if (_.isEqual(params, newParams)) return

      const newQueryString = qs.stringify(newParams, {
        arrayFormat: 'repeat',
        skipNulls: true,
      })

      navigate(`?${newQueryString}`)
    },
    [navigate, params, sorting]
  )

  const tableOptions: TableOptions<EntityReference> = {
    data: getEntityReferenceResponse?.entityReferences ?? [],
    columns,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    pageCount,
    state: {
      pagination,
      sorting,
      columnVisibility,
      columnPinning,
    },
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualPagination: true,
    manualSorting: true,
    enableMultiSort: true,
    maxMultiSortColCount: 2,
    meta: {
      headerRowHeight: 35,
      dataRowHeight: 40,
    },
  }

  // 表示状態が関連情報でない場合は何も表示しない
  if (displayState !== 'relatedInfo') {
    return null
  }

  return (
    <>
      {/* 関連情報テーブル */}
      <CrewTable tableOptions={tableOptions} />

      {/* 削除確認ダイアログ */}
      <CrewConfirmDialog
        isOpen={isConfirmDialogOpen}
        message={t('message.general.confirmMessage.delete')}
        onPermitButtonClick={handleDeletePermitButtonClick}
        onCancelButtonClick={closeConfirmDialog}
        permitButtonDisabled={isLoadingDeleteReference}
      />
    </>
  )
})
